'use client';

import cn from 'classnames';
import { ArticleCarousel } from 'src/components/AiCloud/MlSpaceCarousels/ArticleCarousel';
import { ReviewCarousel } from 'src/components/AiCloud/MlSpaceCarousels/ReviewCarousel';
import { Link } from 'src/components/Link';
import { VideoPlayer } from 'src/components/VideoPlayer';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { Layout } from 'src/layout';
import { Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { Form } from 'src/uikit/Form';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import s from './Mlspace.module.scss';
import { MlspaceData } from './mockMLSpace';

interface PageLinkProps {
  text: string;
  href?: string;
  type?: 'outlined' | 'filled';
  theme?: 'green' | 'black';
  className?: string;
  target?: string;
  id?: string;
  onClick?: () => void;
}

function PageLink({
  text,
  href,
  type,
  theme,
  className = '',
  target,
  id,
  onClick,
}: PageLinkProps) {
  const linkVariant = type === 'outlined' ? [s.outlined] : [s.filled];
  const linkTheme = theme === 'black' ? [s.black] : [s.green];

  return (
    <a
      id={id}
      target={target}
      rel={target ? 'noreferrer' : undefined}
      href={href ? href : undefined}
      className={cn(s.pageLink, linkVariant, linkTheme, className)}
      onClick={onClick}
    >
      {text}
    </a>
  );
}

interface ContainerProps {
  children: React.ReactNode;
  className?: string;
}

function Container({ children, className = '' }: ContainerProps) {
  return <div className={cn(s.container, className)}>{children}</div>;
}

function HeadBlock({
  data,
  image,
  onConsultationButtonClick,
}: {
  data: MlspaceData['head'];
  image: string;
  onConsultationButtonClick: () => void;
}) {
  const { windowWidth } = useWindowWidth();

  const breadcrumbs = [
    { title: 'Главная', link: '/' },
    { title: 'Продукты', link: '/products' },
    { title: 'Платформа Cloud․ru ML Space' },
  ];

  return (
    <div className={s.heroBlock}>
      <div className={s.content}>
        <div>
          <Breadcrumbs
            links={breadcrumbs}
            color={Breadcrumbs.colors.White}
            className={s.breadcrumbs}
          />
          <h1
            className={s.title}
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
          <div className={s.text}>{data.text[0]}</div>
        </div>
        {windowWidth && windowWidth <= 767 && (
          <div className={s.mobileImage}>
            <img
              src={image}
              alt="ML Space"
              data-qa="banner image"
              width={492}
              height={340}
            />
          </div>
        )}
        <div className={s.links}>
          <PageLink
            onClick={onConsultationButtonClick}
            text={data.buttons[0]}
            className="gtm-btn-to-form_mlspace"
            id="mlspace-main"
          />
          <PageLink
            href="/docs/ml-space.html"
            theme="black"
            target="_blank"
            text={data.buttons[1]}
            className="gtm-btn-to-docs_mlspace"
          />
        </div>
      </div>
    </div>
  );
}

function Provide({ data }: { data: MlspaceData['provide'] }) {
  return (
    <Container className={s.provide}>
      <h3>{data.title}</h3>
      <div className={s.cards}>
        {data.items.map(({ title, icon }, index) => {
          return (
            <div className={s.card} key={index}>
              <div
                className={s.cardTitle}
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <img
                className={s.cardIcon}
                src={icon}
                alt="card icon"
                data-qa="card image"
                width={100}
                height={100}
              />
            </div>
          );
        })}
      </div>
    </Container>
  );
}

function Advantage({
  backgroundImage,
  title,
  text,
  items,
}: {
  backgroundImage?: string;
  title: string;
  text?: string;
  items?: string[];
}) {
  return (
    <div
      className={s.block}
      style={{
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
      }}
    >
      <div
        className={s.blockTitle}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {text && <div className={s.blockText}>{text}</div>}
      {items && (
        <ul className={s.blockList}>
          {items?.map((item, index) => (
            <li className={s.blockText} key={index}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Advantages({ data }: { data: MlspaceData['advantages'] }) {
  const firstRowBlocks = [1, 2, 3];
  const secondRowBlocks = [4, 5];

  return (
    <Container className={s.advantages}>
      <h3 className={s.title}>
        {data.title.part1}
        <span className={s.titleNowrap}>{data.title.part2}</span>
        {data.title.part3}
      </h3>
      <div>
        <div className={s.blocks}>
          <Advantage
            backgroundImage="https://cdn.cloud.ru/backend/images/aicloud/mlspace/ml.png"
            title={data.advantages[0].title}
            text={data.advantages[0].text}
          />
          <div>
            {firstRowBlocks.map((id) => (
              <Advantage
                key={id}
                title={data.advantages[id].title}
                text={data.advantages[id].text}
                items={data.advantages[id].items}
              />
            ))}
          </div>
        </div>
        <div className={s.blocks}>
          <div>
            {secondRowBlocks.map((id) => (
              <Advantage
                key={id}
                title={data.advantages[id].title}
                text={data.advantages[id].text}
                items={data.advantages[id].items}
              />
            ))}
          </div>
          <Advantage
            backgroundImage="https://cdn.cloud.ru/backend/images/aicloud/mlspace/stacks.png"
            title={data.advantages[6].title}
            text={data.advantages[6].text}
          />
        </div>
      </div>
    </Container>
  );
}

function Cases({ data }: { data: MlspaceData['cases'] }) {
  return (
    <Container className={s.cases}>
      <h3>{data.title}</h3>
      <ReviewCarousel slides={data.items} />
    </Container>
  );
}

function Video() {
  return (
    <Container className={s.video}>
      <VideoPlayer
        className={s.videoWrap}
        link="https://cdn-video.cloud.ru/backend/video/products/mlspace.mp4"
        previewImage="https://cdn.cloud.ru/backend/images/ml-space/mlspace_preview.webp"
        controls
      />
    </Container>
  );
}

function ProductList({ data }: { data: MlspaceData['productList'] }) {
  return (
    <div className={s.productList}>
      <Container>
        <h3 className={s.title}>{data.title}</h3>
        <div>
          {data.products.map(({ title, text, image, link }, index) => (
            <Link
              href={link}
              className={s.product}
              key={index}
              id={`mlspace-productcard-link-${title}`}
              analyticsOptions={{
                action: link,
                clickZone: 'body',
                clickElement: 'cardclick',
                clickContent: title,
                uniqueId: `mlspace-productcard-link-${title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
                transitionUrl: link,
                transitionTypeMetrics: 'internal',
              }}
            >
              <div className={s.productContent}>
                <div className={s.row}>
                  <img
                    className={s.productImage}
                    src={image}
                    alt={`${title} - изображение`}
                    data-qa="product image"
                    width={28}
                    height={28}
                  />
                  <div className={s.productTitle}>{title}</div>
                </div>
                <div className={s.productText}>{text}</div>
              </div>
              <div className={s.productLink}>
                <img
                  src="/svg/pages/mlspace/chevron-right.svg"
                  alt="arrow"
                  data-qa="chevron image"
                  width={20}
                  height={20}
                />
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </div>
  );
}

function PlatformParts({ data }: { data: MlspaceData['platformParts'] }) {
  return (
    <Container className={s.platformParts}>
      <h3>{data.title}</h3>
      <div className={s.items}>
        {data.parts.map(({ title, text, image }, index) => (
          <div className={s.item} key={index}>
            <img
              className={s.itemImage}
              src={image}
              alt={title}
              data-qa="part image"
              width={36}
              height={36}
            />
            <div className={s.itemTitle}>{title}</div>
            <div className={s.itemText}>{text}</div>
          </div>
        ))}
      </div>
    </Container>
  );
}

function Christofari({ data }: { data: MlspaceData['christofari'] }) {
  return (
    <div className={s.christofari}>
      <Container>
        <h3 className={s.christofariTitle}>{data.title}</h3>
        <div className={s.christofariText}>{data.text}</div>
        <VideoPlayer
          className={s.videoWrap}
          link="https://cdn-video.cloud.ru/backend/video/products/mlspace-2.mp4"
          previewImage="https://cdn.cloud.ru/backend/images/ml-space/mlspace_preview2.webp"
          controls
        />
      </Container>
    </div>
  );
}

function Achievements({ data }: { data: MlspaceData['achievements'] }) {
  return (
    <div className={s.achievements}>
      <h3>{data.title}</h3>
      <div className={s.achievementsList}>
        {data.list.map(({ title, text, link }, index) => {
          if (link) {
            return (
              <a
                className={s.achievement}
                key={index}
                href={link}
                target="_blank"
                rel="noreferrer"
              >
                <div className={s.achievementTitle}>{title}</div>
                <div className={s.achievementText}>{text}</div>
              </a>
            );
          }

          return (
            <div className={s.achievement} key={index}>
              <div className={s.achievementTitle}>{title}</div>
              <div className={s.achievementText}>{text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Webinars({ data }: { data: MlspaceData['webinars'] }) {
  return (
    <div className={s.webinars}>
      <h3>{data.title}</h3>
      <div className={s.webinarsList}>
        {data.list.map(({ title, image, link }, index) => (
          <a
            className={s.webinar}
            target="_blank"
            rel="noreferrer"
            key={index}
            href={link}
          >
            <img
              className={s.webinarImage}
              src={image}
              alt={title}
              data-qa="webinar image"
              width={384}
              height={240}
            />
            <div className={s.webinarText}>{title}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Articles({ data }: { data: MlspaceData['articles'] }) {
  return (
    <div className={s.articles}>
      <h3>{data.title}</h3>
      <ArticleCarousel slides={data.items} />
    </div>
  );
}

function WhiteBackgroundBlocks({
  webinarData,
  achievmentsData,
  articlesData,
}: {
  webinarData: MlspaceData['webinars'];
  achievmentsData: MlspaceData['achievements'];
  articlesData: MlspaceData['articles'];
}) {
  return (
    <div className={s.whiteBgBlocks}>
      <Container>
        <Achievements data={achievmentsData} />
        <Webinars data={webinarData} />
        <Articles data={articlesData} />
      </Container>
    </div>
  );
}

function HelpfulLinks({ data }: { data: MlspaceData['helpfulLinks'] }) {
  return (
    <Container className={s.helpfulLinksBlock}>
      <h3>{data.title}</h3>
      <div className={s.links}>
        {data.links.map(({ name, text, link, target }, index) => (
          <a
            href={link}
            target={target}
            rel={target ? 'noreferrer' : undefined}
            className={`${s.helpfulLink} ${
              index === 0 ? 'gtm-btn-to-docs_mlspace' : ''
            }`}
            key={index}
          >
            <div className={s.helpfulLinkTitle}>
              {name}
              <img
                className={s.helpfulLinkArrow}
                src="https://cdn.cloud.ru/backend/images/aicloud/mlspace/arrow-link.png"
                alt="arrow"
                data-qa="arrow image"
                width={24}
                height={24}
              />
            </div>
            <div className={s.helpfulLinkText}>{text}</div>
          </a>
        ))}
      </div>
    </Container>
  );
}

export function MLSpacePage({ data }: { data: MlspaceData }) {
  const { formRef, setAnchorId } = useIntersectionObserveForms({
    formName: 'lead-platforms',
  });

  const handleButtonClickToForm = (anchorId: string) => {
    scrollToElementWithId('form');
    setAnchorId(anchorId);
  };

  return (
    <Layout>
      <div className={s.root}>
        <HeadBlock
          onConsultationButtonClick={() =>
            handleButtonClickToForm('mlspace_anchor_to_form')
          }
          data={data.head}
          image="https://cdn.cloud.ru/backend/images/aicloud/mlspace/ml-logo.png"
        />
        <Provide data={data.provide} />
        <Advantages data={data.advantages} />
        <Video />
        <Cases data={data.cases} />
        <ProductList data={data.productList} />
        <PlatformParts data={data.platformParts} />
        <Christofari data={data.christofari} />
        <WhiteBackgroundBlocks
          webinarData={data.webinars}
          achievmentsData={data.achievements}
          articlesData={data.articles}
        />
        <HelpfulLinks data={data.helpfulLinks} />
      </div>

      <Form
        id="form"
        legalEntity
        ref={formRef}
        inputPath="v2/order"
        productSlug="ml-space"
        title="Оставьте заявку на консультацию"
        allFormsSentFormType="sale"
        analyticsFieldFill={{ formType: 'body' }}
        formVariant="inner"
        successfulSubmissonFormAnalytics={{
          formname: 'lead-platforms',
        }}
      />
    </Layout>
  );
}
